/**
 * SelfCreateRequestModal — lets Pre-Sales Engineers / Solutions Architects
 * (and Admin / Company Sales Head) raise a pre-sales request for themselves
 * without waiting for an SDR to do it.
 *
 * On submit: POST /api/presales with the picked lead/opportunity, request type,
 * urgency and optional scheduled date. The endpoint auto-assigns to the caller
 * when a PSE raises their own request. An inline `Task` is also created with
 * `task_type: 'demo'` (or matching) so it lights up on the Unified Calendar.
 *
 * Visible only to PSE/SA/Admin roles — the opener button is gated by canCreate.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const REQ_TYPES = [
  { value: 'demo', label: 'Product Demo' },
  { value: 'poc', label: 'Proof of Concept' },
  { value: 'technical_evaluation', label: 'Technical Evaluation' },
  { value: 'solution_design', label: 'Solution Design' },
  { value: 'integration_assessment', label: 'Integration Assessment' },
];

const TASK_TYPE_FOR_REQUEST = {
  demo: 'demo',
  poc: 'meeting',
  technical_evaluation: 'meeting',
  solution_design: 'meeting',
  integration_assessment: 'meeting',
};

export default function SelfCreateRequestModal({ onClose, onCreated, defaultType = 'demo' }) {
  const [form, setForm] = useState({
    scope: 'lead',            // 'lead' | 'opportunity'
    lead_id: '',
    opportunity_id: '',
    request_type: defaultType,
    title: '',
    requirements: '',
    urgency: 'medium',
    scheduled_date: '',
    create_task: true,
  });
  const [leads, setLeads] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState('');

  // Load leads + opportunities (limit 500; users can search within).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/leads?limit=500`).then(r => r.data?.leads || r.data?.data || r.data || []).catch(() => []),
      axios.get(`${API}/opportunities?limit=500`).then(r => r.data?.opportunities || r.data?.data || r.data || []).catch(() => []),
    ]).then(([l, o]) => {
      if (cancelled) return;
      setLeads(Array.isArray(l) ? l : []);
      setOpps(Array.isArray(o) ? o : []);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const filteredLeads = leads.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (l.name || '').toLowerCase().includes(q)
      || (l.organization || '').toLowerCase().includes(q)
      || (l.email || '').toLowerCase().includes(q);
  }).slice(0, 100);

  const filteredOpps = opps.filter((o) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (o.name || '').toLowerCase().includes(q)
      || (o.company_name || '').toLowerCase().includes(q);
  }).slice(0, 100);

  const canSubmit = form.title.trim()
    && ((form.scope === 'lead' && form.lead_id) || (form.scope === 'opportunity' && form.opportunity_id));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        request_type: form.request_type,
        title: form.title.trim(),
        requirements: form.requirements.trim(),
        urgency: form.urgency,
      };
      if (form.scope === 'lead') payload.lead_id = form.lead_id;
      else payload.opportunity_id = form.opportunity_id;
      if (form.scheduled_date) payload.scheduled_date = new Date(form.scheduled_date).toISOString();

      const res = await axios.post(`${API}/presales`, payload);
      const req = res.data;

      // Optionally drop a Task on the calendar tied to this request — gives
      // the PSE the same calendar visibility as any other scheduled activity.
      if (form.create_task && form.scheduled_date) {
        try {
          await axios.post(`${API}/tasks`, {
            title: `${REQ_TYPES.find(t => t.value === form.request_type)?.label || 'Request'}: ${form.title.trim()}`,
            due_date: new Date(form.scheduled_date).toISOString(),
            priority: form.urgency === 'critical' || form.urgency === 'high' ? 'high' : 'medium',
            task_type: TASK_TYPE_FOR_REQUEST[form.request_type] || 'meeting',
            status: 'not_started',
            lead_id: form.scope === 'lead' ? form.lead_id : undefined,
            description: `Linked to pre-sales request #${req?.id || ''}`,
          });
        } catch (_) {
          // Non-fatal — request was created, just the calendar tile might be missing.
        }
      }

      toast.success('Pre-sales request created');
      onCreated && onCreated(req);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="self-create-presales-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Raise Pre-Sales Request</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Schedule a demo, POC or evaluation for yourself. Auto-assigned to you.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="self-presales-close">✕</button>
        </div>

        {/* Scope switch: Lead vs Opportunity */}
        <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden">
          {['lead', 'opportunity'].map((s) => (
            <button
              key={s}
              onClick={() => setForm({ ...form, scope: s, lead_id: '', opportunity_id: '' })}
              className={`px-3 py-1.5 text-xs font-medium capitalize ${form.scope === s ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200' : 'text-slate-600 dark:text-slate-300'}`}
              data-testid={`self-presales-scope-${s}`}
            >
              For {s}
            </button>
          ))}
        </div>

        {/* Search + picker */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
            Search {form.scope === 'lead' ? 'leads' : 'opportunities'}
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="name, company, email…"
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid="self-presales-search"
          />
          <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg">
            {loading && <p className="p-3 text-xs text-slate-400">Loading…</p>}
            {!loading && form.scope === 'lead' && filteredLeads.map((l) => (
              <button
                key={l.id}
                onClick={() => setForm({ ...form, lead_id: l.id, title: form.title || `${REQ_TYPES.find(t => t.value === form.request_type)?.label} for ${l.name}` })}
                className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 dark:border-slate-700 last:border-0 ${form.lead_id === l.id ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                data-testid={`self-presales-lead-${l.id}`}
              >
                <div className="font-medium text-slate-800 dark:text-white">{l.name}</div>
                <div className="text-xs text-slate-500">{l.organization || '—'} · {l.email || l.phone || ''}</div>
              </button>
            ))}
            {!loading && form.scope === 'opportunity' && filteredOpps.map((o) => (
              <button
                key={o.id}
                onClick={() => setForm({ ...form, opportunity_id: o.id, title: form.title || `${REQ_TYPES.find(t => t.value === form.request_type)?.label} for ${o.name}` })}
                className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 dark:border-slate-700 last:border-0 ${form.opportunity_id === o.id ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                data-testid={`self-presales-opp-${o.id}`}
              >
                <div className="font-medium text-slate-800 dark:text-white">{o.name}</div>
                <div className="text-xs text-slate-500">{o.company_name || '—'} · stage: {o.stage || '—'}</div>
              </button>
            ))}
            {!loading && form.scope === 'lead' && filteredLeads.length === 0 && <p className="p-3 text-xs text-slate-400">No leads match.</p>}
            {!loading && form.scope === 'opportunity' && filteredOpps.length === 0 && <p className="p-3 text-xs text-slate-400">No opportunities match.</p>}
          </div>
        </div>

        {/* Request Type + Urgency */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Request Type</label>
            <select
              value={form.request_type}
              onChange={(e) => setForm({ ...form, request_type: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              data-testid="self-presales-type"
            >
              {REQ_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Urgency</label>
            <select
              value={form.urgency}
              onChange={(e) => setForm({ ...form, urgency: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              data-testid="self-presales-urgency"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        {/* Title + Requirements */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Contact Center demo for Acme"
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid="self-presales-title"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Requirements / Notes</label>
          <textarea
            rows={3}
            value={form.requirements}
            onChange={(e) => setForm({ ...form, requirements: e.target.value })}
            placeholder="What will you cover / what does the customer need?"
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid="self-presales-requirements"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Scheduled for</label>
          <input
            type="datetime-local"
            value={form.scheduled_date}
            onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            data-testid="self-presales-scheduled"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={form.create_task}
            onChange={(e) => setForm({ ...form, create_task: e.target.checked })}
            data-testid="self-presales-task-flag"
          />
          Also create a calendar task (shows on Activities → Calendar)
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="self-presales-submit"
          >
            {submitting ? 'Creating…' : 'Raise Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
